/**
 * ImplosionSkillEffect — Astral rift path evolution: two-activation tear.
 *
 * Replaces Rift at XP 1200. Extends the single-activation gravity well model with
 * a two-press collapse mechanic:
 *
 *   1st activation (tear closed): Opens the gravitational tear exactly as Rift —
 *     places a GravityPullSystem well + edge DoT zone, sets _tearActive = true.
 *     Does NOT enter cooldown. Remains hot for the second press.
 *
 *   2nd activation (tear open): Collapses the tear early — removes the well,
 *     deals burst damage (baseBurstDamage × falloff) to all enemies within the
 *     pull radius at the moment of collapse, then enters cooldown.
 *
 *   Natural expiration: Well duration runs out without a second activation — tear
 *     closes without burst damage, cooldown starts normally (inherited _expireWell).
 *
 * Folded Space interaction (rift_path_passive): If 2+ enemies are inside the pull
 * radius at collapse, fires CooldownHookSystem.notify with conditionId
 * 'implosion_collapse_2plus', triggering the registered 30% Phase Step refund.
 *
 * Upgrade inheritance: queries resolver with scope 'rift' for all rift_pull and
 * rift_duration bonuses, identical to RiftSkillEffect.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § implosion
 * @see design-decisions.md § Astral Base Values (burst damage 25)
 * @see ADR-064 (attunement shared systems architecture)
 */

import { RiftSkillEffect, type RiftDeps, type RiftConfig } from './RiftSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency extensions
// ---------------------------------------------------------------------------

export interface ImplosionDeps extends RiftDeps {
  /** Returns IDs of enemies within a circular radius (in tiles) of a point. */
  readonly enemies: {
    getEnemiesInRadius(x: number, y: number, radiusTiles: number): string[];
  };
  /** Deals burst damage to a single enemy entity. */
  readonly combat: {
    dealDamage(enemyId: string, damage: number): void;
  };
  /** Notifies a condition event to registered cooldown hooks. */
  readonly cooldownHooks: {
    notify(event: { type: 'onCondition'; conditionId: string }): void;
  };
}

// ---------------------------------------------------------------------------
// Config extensions
// ---------------------------------------------------------------------------

export interface ImplosionConfig extends RiftConfig {
  /** Flat burst damage applied to all enemies inside the pull radius on collapse. */
  readonly baseBurstDamage: number;
  /**
   * Minimum enemies in pull radius at collapse to trigger Folded Space refund.
   * Reads from rift_path_passive unlock data; default 2.
   */
  readonly enemyCountThreshold: number;
}

const IMPLOSION_DEFAULTS: Partial<ImplosionConfig> = {
  baseBurstDamage: 25,
  enemyCountThreshold: 2,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ImplosionSkillEffect extends RiftSkillEffect {
  override readonly skillId = 'implosion';

  private readonly implosionDeps: ImplosionDeps;
  readonly implosionConfig: ImplosionConfig;

  private _tearActive = false;
  private _tearPosition: { x: number; y: number } | null = null;
  private _collapseCount = 0;
  private _lastBurstEnemyCount = 0;

  constructor(deps: ImplosionDeps, config: Partial<ImplosionConfig> = {}) {
    super(deps, config);
    this.implosionDeps = deps;
    this.implosionConfig = {
      ...(this.config as RiftConfig),
      ...IMPLOSION_DEFAULTS,
      ...config,
    } as ImplosionConfig;
  }

  // --- State getters ---

  get tearActive(): boolean { return this._tearActive; }
  get collapseCount(): number { return this._collapseCount; }
  get lastBurstEnemyCount(): number { return this._lastBurstEnemyCount; }

  // --- SkillEffect interface ---

  /**
   * Returns true when idle (can open a new tear) or when a tear is already
   * active (can collapse early). Blocked only during cooldown.
   */
  override canActivate(): boolean {
    if (this.cooldown > 0) return false;
    return !this._tearActive || this.isWellActive;
  }

  /**
   * Branches on tear state:
   * - If tear is closed → open the tear (delegated to RiftSkillEffect.activate).
   * - If tear is open → collapse early for burst damage.
   */
  override activate(): boolean {
    if (this.cooldown > 0) return false;

    if (!this._tearActive) {
      // First press: open the tear.
      const result = super.activate();
      if (result) {
        this._tearActive = true;
        // Use the clamped position recorded by the parent during well placement.
        this._tearPosition = this._lastPlacedPosition ? { ...this._lastPlacedPosition } : null;
        // Do NOT start cooldown — super.activate() doesn't either until _expireWell.
      }
      return result;
    } else {
      // Second press: collapse the tear.
      return this._collapse();
    }
  }

  // --- Private helpers ---

  private _collapse(): boolean {
    if (!this.isWellActive || this._tearPosition === null) return false;

    // Count enemies in pull radius for Folded Space check.
    const enemies = this.implosionDeps.enemies.getEnemiesInRadius(
      this._tearPosition.x,
      this._tearPosition.y,
      this.resolvedRadiusTiles,
    );
    this._lastBurstEnemyCount = enemies.length;

    // Deal burst damage to all enemies in radius.
    for (const enemyId of enemies) {
      this.implosionDeps.combat.dealDamage(enemyId, this.implosionConfig.baseBurstDamage);
    }

    // Fire Folded Space condition if threshold met.
    if (enemies.length >= this.implosionConfig.enemyCountThreshold) {
      this.implosionDeps.cooldownHooks.notify({
        type: 'onCondition',
        conditionId: 'implosion_collapse_2plus',
      });
    }

    // Clean up the well and enter cooldown.
    this._expireWell();
    this._tearActive = false;
    this._tearPosition = null;
    this._collapseCount++;

    return true;
  }

  /**
   * Natural expiration: well timer ran out without a second activation.
   * No burst damage. Reset tear state before the parent re-uses the cooldown.
   */
  protected override _expireWell(): void {
    this._tearActive = false;
    this._tearPosition = null;
    super._expireWell();
  }

  override destroy(): void {
    this._tearActive = false;
    this._tearPosition = null;
    super.destroy();
  }
}
